import { useMemo } from "react"
import Box from "@mui/material/Box"
import dayjs from "dayjs"
import { EChartsOption } from "echarts"
import { Icon, IconColor } from "../shared/Icon"
import {
   LevelVitalsGraphResponse,
   VitalsGraphChartProps,
} from "@/api/FacilityLevelsService"
import Chart from "@/components/shared/Chart"
import { localizeDateTime } from "@/utils/index"
import "./index.scss"

export function buildVitalTrendOption(
   data: LevelVitalsGraphResponse | null,
   timezone: string
): EChartsOption {
   if (!data) return {}

   // ECharts places time ticks only in the browser zone or in UTC. Plotting the
   // facility's wall-clock time on a UTC axis puts the ticks on its round times.
   const toAxisValue = (timestamp: string) =>
      dayjs(timestamp).tz(timezone).utc(true).valueOf()

   const hrIndex = data.header.indexOf("heart_rate")
   const rrIndex = data.header.indexOf("respiratory_rate")
   const mviIndex = data.header.indexOf("movement_index")

   const hrData: [number, number][] = []
   const rrData: [number, number][] = []
   const mviData: [number, number | null][] = []

   Object.entries(data.data).forEach(([timestamp, row]) => {
      const ts = toAxisValue(timestamp)
      const hr = row[hrIndex]
      const rr = row[rrIndex]
      const mvi = row[mviIndex]
      if (hr !== null && hr !== undefined) hrData.push([ts, hr])
      if (rr !== null && rr !== undefined) rrData.push([ts, rr])
      mviData.push([ts, mvi ?? null])
   })

   hrData.sort((a, b) => a[0] - b[0])
   rrData.sort((a, b) => a[0] - b[0])
   mviData.sort((a, b) => a[0] - b[0])

   return {
      useUTC: true,
      tooltip: {
         trigger: "axis",
         formatter: (params) => {
            const items = (Array.isArray(params)
               ? params
               : [params]) as unknown as {
               axisValue: number
               marker: string
               seriesName: string
               value: [number, number | null]
            }[]
            if (!items.length) return ""
            const ts = localizeDateTime(dayjs.utc(items[0].axisValue), null)
            const lines = items.map((point) => {
               const seriesValue =
                  point.value[1] != null ? Math.round(point.value[1]) : "-"
               return `${point.marker} ${point.seriesName}: ${seriesValue}`
            })
            return [ts, ...lines].join("<br/>")
         },
      },
      legend: { top: 0, data: ["BPM", "RPM", "MVI"] },
      grid: { top: 48, bottom: 40, left: 100, right: 56 },
      xAxis: {
         type: "time",
         axisLabel: {
            formatter: (value: number) =>
               dayjs.utc(value).format("MMM D HH:mm"),
         },
      },
      yAxis: [
         {
            type: "value",
            name: "BPM",
            nameLocation: "middle",
            nameGap: 40,
            min: 0,
            position: "left",
            offset: 50,
         },
         {
            type: "value",
            name: "RPM",
            nameLocation: "middle",
            nameGap: 40,
            min: 0,
            position: "left",
            splitLine: { show: false },
         },
         {
            type: "value",
            name: "MVI",
            nameLocation: "middle",
            nameGap: 40,
            min: 0,
            position: "right",
            splitLine: { show: false },
         },
      ],
      dataZoom: [{ type: "inside" }],
      series: [
         {
            name: "BPM",
            type: "scatter",
            yAxisIndex: 0,
            data: hrData,
            symbolSize: 4,
            itemStyle: { color: "#8567AD" },
         },
         {
            name: "RPM",
            type: "scatter",
            yAxisIndex: 1,
            data: rrData,
            symbolSize: 4,
            itemStyle: { color: "#EDB73B" },
         },
         {
            name: "MVI",
            type: "bar",
            yAxisIndex: 2,
            data: mviData,
            itemStyle: {
               color: "#43A047",
            },
         },
      ],
   }
}

const VitalTrendChart = ({
   data,
   loading,
   timezone,
}: VitalsGraphChartProps) => {
   const option = useMemo(
      () => buildVitalTrendOption(data, timezone),
      [data, timezone]
   )
   const isEmpty = !data || Object.keys(data.data).length === 0

   return (
      <Box sx={{ width: "100%" }}>
         <div className="vital-trend-chart__title">
            <Icon
               name="graph"
               color={IconColor.HONOLULU_BLUE}
            />
            Vital Sign History
         </div>
         {isEmpty && !loading ? (
            <div className="vital-trend-chart--empty">
               No vitals recorded for this range.
            </div>
         ) : (
            <Chart
               option={option}
               loading={loading}
               height={320}
            />
         )}
      </Box>
   )
}

export default VitalTrendChart
